import { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { AuthLayout } from "@/components/AuthLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, formatApiError } from "@/lib/api";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [form, setForm] = useState({ password: "", confirm: "" });
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.password.length < 6) {
      setError("Das Passwort muss mindestens 6 Zeichen lang sein.");
      return;
    }
    if (form.password !== form.confirm) {
      setError("Die Passwörter stimmen nicht überein.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password: form.password });
      setDone(true);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <AuthLayout title="Ungültiger Link" testid="reset-invalid-page">
        <p className="text-center text-charcoal/70">
          Dieser Link ist ungültig. Bitte fordern Sie einen neuen an.
        </p>
        <Link to="/passwort-vergessen">
          <Button className="w-full mt-8 bg-gold hover:bg-golddark text-white rounded-sm py-6 transition-colors duration-500">
            Neuen Link anfordern
          </Button>
        </Link>
      </AuthLayout>
    );
  }

  if (done) {
    return (
      <AuthLayout title="Passwort geändert" testid="reset-success-page">
        <p className="text-center text-charcoal/70" data-testid="reset-success-message">
          Ihr Passwort wurde erfolgreich zurückgesetzt.
        </p>
        <Button
          onClick={() => navigate("/login")}
          className="w-full mt-8 bg-gold hover:bg-golddark text-white rounded-sm py-6 transition-colors duration-500"
          data-testid="reset-to-login-button"
        >
          Zur Anmeldung
        </Button>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title="Neues Passwort" testid="reset-password-page">
      <form onSubmit={submit} className="space-y-6">
        {error && (
          <p className="text-sm text-destructive bg-destructive/5 border border-destructive/20 rounded-sm px-4 py-3" data-testid="reset-error">
            {error}
          </p>
        )}
        <div className="space-y-2">
          <Label htmlFor="password">Neues Passwort</Label>
          <Input
            id="password"
            type="password"
            required
            value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
            data-testid="reset-password-input"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="confirm">Passwort bestätigen</Label>
          <Input
            id="confirm"
            type="password"
            required
            value={form.confirm}
            onChange={(e) => setForm({ ...form, confirm: e.target.value })}
            data-testid="reset-confirm-input"
          />
        </div>
        <Button
          type="submit"
          disabled={loading}
          className="w-full bg-gold hover:bg-golddark text-white rounded-sm py-6 transition-colors duration-500"
          data-testid="reset-submit-button"
        >
          {loading ? "Wird gespeichert…" : "Passwort speichern"}
        </Button>
      </form>
    </AuthLayout>
  );
}
